import { useState, useEffect, useCallback } from 'react';
import { DateTimeSelection } from 'accounts/components/multiselect-date-time-range-picker/multiselect-date-time-range-picker';
import {
  getSelectedSelections,
  setSelectedSelections,
  getSelectedDate,
  setSelectedDate,
  getSpecificRequests,
  setSpecificRequests,
  updateInspectionData,
  clearInspectionData,
  getAllInspectionData,
} from 'accounts/utils/inspection-schedule-storage';

export function useInspectionScheduleStorage() {
  // Initialize state from sessionStorage
  const [selectedSelections, setSelectedSelectionsState] = useState<
    DateTimeSelection[]
  >(() => getSelectedSelections());

  const [selectedDate, setSelectedDateState] = useState<string | null>(() =>
    getSelectedDate(),
  );

  const [specificRequests, setSpecificRequestsState] = useState<string>(() =>
    getSpecificRequests(),
  );

  // Wrapper functions that update both state and sessionStorage
  const handleSetSelectedSelections = useCallback(
    (selections: DateTimeSelection[]) => {
      setSelectedSelectionsState(selections);
      setSelectedSelections(selections);
    },
    [],
  );

  const handleSetSelectedDate = useCallback((date: string | null) => {
    setSelectedDateState(date);
    setSelectedDate(date);
  }, []);

  const handleSetSpecificRequests = useCallback((requests: string) => {
    setSpecificRequestsState(requests);
    setSpecificRequests(requests);
  }, []);

  const handleUpdateData = useCallback(
    (updates: {
      selectedSelections?: DateTimeSelection[];
      selectedDate?: string | null;
      specificRequests?: string;
    }) => {
      if (updates.selectedSelections !== undefined) {
        setSelectedSelectionsState(updates.selectedSelections);
      }
      if (updates.selectedDate !== undefined) {
        setSelectedDateState(updates.selectedDate);
      }
      if (updates.specificRequests !== undefined) {
        setSpecificRequestsState(updates.specificRequests);
      }
      updateInspectionData(updates);
    },
    [],
  );

  const handleClearAll = useCallback(() => {
    setSelectedSelectionsState([]);
    setSelectedDateState(null);
    setSpecificRequestsState('');
    clearInspectionData();
  }, []);

  // Sync with sessionStorage on mount (in case data was updated elsewhere)
  useEffect(() => {
    const data = getAllInspectionData();
    setSelectedSelectionsState(data.selectedSelections);
    setSelectedDateState(data.selectedDate);
    setSpecificRequestsState(data.specificRequests);
  }, []);

  return {
    // State
    selectedSelections,
    selectedDate,
    specificRequests,

    // Actions
    setSelectedSelections: handleSetSelectedSelections,
    setSelectedDate: handleSetSelectedDate,
    setSpecificRequests: handleSetSpecificRequests,
    updateData: handleUpdateData,
    clearAll: handleClearAll,
  };
}
